import { OrderSearchParams, OrderStatusCode } from '@/console/api/types'

/**
 * LOGISPRD-13034 — F-OPT 주문 조회 패널의 순수 로직. 계산은 전부 여기 두고 `OrderSearchPanel` 은
 * 결과만 그린다.
 *
 * 조건 규칙은 서버(`SearchDevConsoleOrdersService`) 400 규칙과 같다 — 운영 `standard_order` 가
 * 2천만 행대라 인덱스를 타는 조건이 없으면 서버가 거절한다. 같은 규칙을 조회 버튼 활성화 조건으로
 * 두어 사용자가 400 을 볼 일이 없게 한다(인계서 "조건 규칙 — 화면에서 먼저 막아 주세요").
 */
export type OrderSearchForm = {
  outboundCutSeqId: string
  orderNumber: string
  refNo1: string
  orderOptionNumber: string
  orderStatuses: OrderStatusCode[]
  collectedDateFrom: string
  collectedDateTo: string
}

export const EMPTY_ORDER_SEARCH_FORM: OrderSearchForm = {
  outboundCutSeqId: '',
  orderNumber: '',
  refNo1: '',
  orderOptionNumber: '',
  orderStatuses: [],
  collectedDateFrom: '',
  collectedDateTo: '',
}

export const ORDER_SEARCH_LIMITS = [20, 50, 100] as const
export const DEFAULT_ORDER_SEARCH_LIMIT = 50
export const MAX_COLLECTED_RANGE_DAYS = 7

export type SearchRule = { ok: true; basis: string } | { ok: false; severity: 'hint' | 'error'; message: string }

const DAY_MS = 24 * 60 * 60 * 1000

/** `yyyy-MM-dd` 두 개의 달력 일수(양 끝 포함). 시간대 영향을 피하려고 UTC 자정으로 센다. */
const inclusiveDays = (from: string, to: string): number => {
  const utc = (d: string): number => {
    const [y, m, day] = d.split('-').map(Number)
    return Date.UTC(y, m - 1, day)
  }
  return Math.round((utc(to) - utc(from)) / DAY_MS) + 1
}

export const validateOrderSearch = (form: OrderSearchForm): SearchRule => {
  const cut = form.outboundCutSeqId.trim()
  const orderNumber = form.orderNumber.trim()
  const refNo1 = form.refNo1.trim()
  const option = form.orderOptionNumber.trim()
  const { collectedDateFrom: from, collectedDateTo: to, orderStatuses } = form

  if (cut && !/^\d+$/.test(cut)) {
    return { ok: false, severity: 'error', message: '출고 컷 차수 id 는 숫자만 넣어 주세요' }
  }

  let days: number | null = null
  if (Boolean(from) !== Boolean(to)) {
    return { ok: false, severity: 'error', message: '수집일은 시작과 끝을 함께 넣어 주세요' }
  }
  if (from && to) {
    days = inclusiveDays(from, to)
    if (days < 1) {
      return { ok: false, severity: 'error', message: '수집일 시작이 끝보다 뒤예요' }
    }
    if (days > MAX_COLLECTED_RANGE_DAYS) {
      return { ok: false, severity: 'error', message: `수집일은 최대 ${MAX_COLLECTED_RANGE_DAYS}일이에요 (지금 ${days}일)` }
    }
  }

  if (cut) return { ok: true, basis: '출고 컷 차수 id' }
  if (orderNumber) return { ok: true, basis: '주문번호' }
  if (refNo1) return { ok: true, basis: '원주문번호' }
  if (orderStatuses.length > 0 && days !== null) {
    return { ok: true, basis: `주문 상태 ${orderStatuses.length}개 + 수집일 ${days}일` }
  }

  if (option) {
    return { ok: false, severity: 'error', message: '옵션번호만으로는 조회할 수 없어요 — 주문번호나 원주문번호를 함께 넣어 주세요' }
  }
  if (orderStatuses.length > 0) {
    return { ok: false, severity: 'error', message: '주문 상태로 찾으려면 수집일(최대 7일)도 넣어 주세요' }
  }
  return { ok: false, severity: 'hint', message: '출고 컷 차수 id · 주문번호 · 원주문번호 중 하나, 또는 주문 상태 + 수집일이 필요해요' }
}

export const toSearchParams = (form: OrderSearchForm, limit: number): OrderSearchParams => {
  const params: OrderSearchParams = { limit }
  const cut = form.outboundCutSeqId.trim()
  if (cut) params.outboundCutSeqId = Number(cut)
  if (form.orderNumber.trim()) params.orderNumber = form.orderNumber.trim()
  if (form.refNo1.trim()) params.refNo1 = form.refNo1.trim()
  if (form.orderOptionNumber.trim()) params.orderOptionNumber = form.orderOptionNumber.trim()
  if (form.orderStatuses.length > 0) params.orderStatuses = form.orderStatuses
  if (form.collectedDateFrom && form.collectedDateTo) {
    params.collectedDateFrom = form.collectedDateFrom
    params.collectedDateTo = form.collectedDateTo
  }
  return params
}

type Tone = 'good' | 'warn' | 'crit' | 'neutral'

const ORDER_STATUS_META: Record<OrderStatusCode, { label: string; tone: Tone }> = {
  HOLD: { label: '출고보류', tone: 'warn' },
  ORDER_COLLECTED: { label: '주문수집', tone: 'neutral' },
  ORDER_ACCEPTED: { label: '주문접수', tone: 'neutral' },
  INV_UNALLOCATED: { label: '재고미할당', tone: 'warn' },
  INV_ALLOCATED: { label: '재고할당', tone: 'neutral' },
  SPLIT_COMPLETED: { label: '분할완료', tone: 'neutral' },
  REPLANNED: { label: '출고재계획', tone: 'warn' },
  CANCELED: { label: '취소', tone: 'crit' },
  CLOSED: { label: '수집 후 미처리', tone: 'crit' },
  OUTBOUND_REQUESTED: { label: '출고지시', tone: 'neutral' },
  PARTIAL_OUTBOUND: { label: '부분출고', tone: 'warn' },
  OUTBOUND_COMPLETED: { label: '출고완료', tone: 'good' },
}

/** 화면 칩 묶음 — 시안 v1 의 앞단 / 할당 / 출고 세 줄. */
export const ORDER_STATUS_GROUPS: { label: string; codes: OrderStatusCode[] }[] = [
  { label: '앞단', codes: ['HOLD', 'ORDER_COLLECTED', 'ORDER_ACCEPTED', 'CLOSED', 'CANCELED'] },
  { label: '할당', codes: ['INV_UNALLOCATED', 'INV_ALLOCATED', 'SPLIT_COMPLETED', 'REPLANNED'] },
  { label: '출고', codes: ['OUTBOUND_REQUESTED', 'PARTIAL_OUTBOUND', 'OUTBOUND_COMPLETED'] },
]

/** 서버에 새 상태가 생겨도 화면이 깨지지 않게 모르는 코드는 원문 · neutral 로 낸다. */
export const orderStatusMeta = (code: string | null): { label: string; tone: Tone } => {
  if (!code) return { label: '—', tone: 'neutral' }
  return ORDER_STATUS_META[code as OrderStatusCode] ?? { label: code, tone: 'neutral' }
}

const ORDER_TYPE_LABEL: Record<string, string> = {
  NORMAL: '일반',
  PLUS: 'PLUS',
  INTER: '사내',
  INTER_OVERSEAS: '사내(해외)',
  BOUTIQUE: '명품',
  RETURN: '반품',
  OVERSEAS: '해외',
  OVERSEAS_LOCAL_SHIPPING: '해외(현지)',
  EXCHANGE: '교환',
  ETC: '기타',
  UNKNOWN: '미지정',
}

export const orderTypeLabel = (code: string | null): string => (code ? (ORDER_TYPE_LABEL[code] ?? code) : '—')

export const toggleId = (selected: number[], id: number, checked: boolean): number[] => {
  if (checked) return selected.includes(id) ? selected : [...selected, id]
  return selected.filter((v) => v !== id)
}

export const togglePage = (selected: number[], pageIds: number[], checked: boolean): number[] =>
  checked ? pageIds.reduce((acc, id) => toggleId(acc, id, true), selected) : selected.filter((v) => !pageIds.includes(v))
